import { useState, useEffect } from "react";
import type { AxiosInstance } from "axios";
import { readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";
import { fetchFwaasOverview } from "./overviewRequest";
import { NO_OVERVIEW, type FwaasOverview } from "./overview.types";

/**
 * Load the Virtual Firewall overview, apart from the detail: the detail paints at once,
 * the overview's cards fill in when FortiManager answers (~1–2 s).
 *
 * `overview` is `null` only while the first reading is on its way. A request that fails
 * leaves every section `null` — each card then says "No reading" — rather than an error
 * screen: the rest of the page is still worth seeing.
 */
export function useFwaasOverview(
    fwaasId: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    const cacheKey =
        fwaasId && tenantId != null
            ? `kontroledge/fwaas/${fwaasId}/overview?tenantId=${tenantId}`
            : null;
    const [overview, setOverview] = useState<FwaasOverview | null>(
        () => readCachedFetch<FwaasOverview>(cacheKey) ?? null
    );

    useEffect(() => {
        if (!fwaasId || tenantId == null) {
            setOverview(null);
            return;
        }

        let cancelled = false;
        // Another service on the same mounted page: its own cached reading or the
        // skeleton — never the previous firewall's numbers.
        const cached = readCachedFetch<FwaasOverview>(cacheKey);
        setOverview(cached ?? null);

        fetchFwaasOverview(api, tenantId, fwaasId)
            .then((loaded) => {
                if (cancelled) return;
                writeCachedFetch(cacheKey, loaded);
                setOverview(loaded);
            })
            .catch(() => {
                if (cancelled) return;
                // A cached reading on screen stays; with none, every card says so.
                if (!cached) setOverview(NO_OVERVIEW);
            });

        return () => {
            cancelled = true;
        };
    }, [fwaasId, tenantId, api, refresh, cacheKey]);

    return overview;
}
